import TableWithPagination from "@components/Tables/TableWithPagination";
import { ConfirmDeleteModal, ModifyRoleModal, PermissionInfoPopup, PermissionGuidePopup } from "@components/UI";
import queryKeys from "@configs/react-query/query-key";
import { showErrorToast, showSuccessToast } from "@configs/react-toastify/snackbar";
import { PermissionContext } from "@contexts/PermissionContext";
import { useMutationData } from "@hooks/useMutationData";
import { useTheme } from "@hooks/useTheme";
import { formatDateTime, uppercase, isNotNullAndEmptyArray } from "@utils/common";
import UserNameWithId from "@components/UI/UserNameWithId";
import { refreshPaginatedListAfterDelete } from "@utils/pagination";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DeleteIcon, EditIcon } from "@components/Icons";
import { ITEMS_PER_PAGE_KEY, DEFAULT_ITEMS_PER_PAGE } from "@constants/table";
import useItemsPerPage from "@hooks/useItemsPerPage";

export default function RoleList() {
    const { t } = useTranslation();
    const { hasDeveloperPermission } = useContext(PermissionContext);
    const { colors } = useTheme();

    const [roleList, setRoleList] = useState([]);
    const [selectedRole, setSelectedRole] = useState(null);
    const [selectedRows, setSelectedRows] = useState([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const { itemsPerPage, handleItemsPerPageChange } = useItemsPerPage(ITEMS_PER_PAGE_KEY.ROLE_LIST, DEFAULT_ITEMS_PER_PAGE);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [totalData, setTotalData] = useState(0);

    useEffect(() => {
        fetchData();
    }, [currentPage, itemsPerPage]);

    const columns = ["roleName", "permissions", "creator", "createdAt", "action"];

    const columnConfig = {
        roleName: {
            style: {
                minWidth: "130px",
                whiteSpace: "normal",
                overflowWrap: "initial",
            },
            headerRender: () => t("role.role-name"),
            customRender: (value, row) => row.code || "",
        },
        permissions: {
            style: {
                width: "40%",
                minWidth: "350px",
                whiteSpace: "normal",
                overflowWrap: "initial",
            },
            headerRender: () => (
                <span className="inline-flex items-center">
                    {t("role.permissions")}
                    <PermissionGuidePopup data-testid="role-list-permission-guide" />
                </span>
            ),
            customRender: (value, row) => {
                // Handle permissions array from the API response
                if (Array.isArray(row.permissions)) {
                    return (
                        <div className="flex flex-wrap gap-2">
                            {row.permissions.map((permission, index) => {
                                const permissionCd = permission.permissionCd || "";
                                const label = permission.permissionCdName || permissionCd || "";
                                return (
                                    <PermissionInfoPopup
                                        key={`${permissionCd}-${index}`}
                                        permissionCode={permissionCd}
                                        permissionLabel={label}
                                        data-testid={`role-list-permission-${permissionCd}-info`}
                                    >
                                        <span
                                            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium h-6 ${colors.bgCommunicationFilterButton} ${colors.textPrimary}`}
                                        >
                                            {label}
                                        </span>
                                    </PermissionInfoPopup>
                                );
                            })}
            
                        </div>
                    );
                }
                return <span className={colors.textNeutralMuted}>No permissions</span>;
            },
        },
        creator: {
            style: {
                minWidth: "100px",
                whiteSpace: "normal",
                overflowWrap: "initial",
            },
            headerRender: () => t("system.creator"),
            customRender: (value, row) => <UserNameWithId name={row.createrName} id={row.createrId} />,
        },
        createdAt: {
            style: {
                minWidth: "180px",
                whiteSpace: "normal",
                overflowWrap: "initial",
            },
            headerRender: () => t("common.create-at"),
            customRender: (value, row) => formatDateTime(row.createdAt) || "",
        },
        action: {
            style: {
                width: "96px",
                whiteSpace: "normal",
                overflowWrap: "initial",
                position: "sticky",
                right: "0",
                zIndex: "10",
                backgroundColor: "inherit",
            },
            headerRender: () => t("common.action"),
            customRender: (value, row) => (
                <div className="flex items-center space-x-2">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onClickEditRole(row);
                        }}
                        className={`${colors.textMuted} ${colors.hoverTextNeutral}`}
                    >
                        <EditIcon className="h-[1.125rem] w-[1.125rem]" />
                    </button>
                    <div className={`w-px h-3 ${colors.bgCommunicationFilterButton} self-center`}></div>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteRole(row.codeSeq);
                        }}
                        className={`${colors.textDanger} ${colors.hoverTextDangerStrong}`}
                    >
                        <DeleteIcon className="h-4 w-4" />
                    </button>
                </div>
            ),
        },
    };

    const updateRoleList = useMutationData(
        queryKeys.updateRoleList,
        { method: "PUT" },
        {
            onSuccess: (response) => { },
        },
    );

    const handleConfirmDelete = async () => {
        const deleteRequestBodies = selectedRows.map(seq => ({
            codeSeq: seq,
            delete: true
        }));
        setDeleteLoading(true);
        try {
            updateRoleList.mutate(deleteRequestBodies, {
                onSuccess: () => {
                    showSuccessToast(t("role.delete-role-success", { count: selectedRows.length }));
                    refreshPaginatedListAfterDelete({
                        currentPage,
                        setCurrentPage,
                        itemsPerPage,
                        totalData,
                        deletedCount: selectedRows.length,
                        fetchData,
                    });
                },
                onError: (error) => {
                    if (error.response?.status === 409) {
                        showErrorToast(t("status.error"), t("role.delete-role-error-conflict", { roleNames: error?.response?.data?.message }));
                    } else {
                        showErrorToast(t("status.error"), t("role.delete-role-error"));
                    }
                    if (isNotNullAndEmptyArray(error.response.data?.deletedSeqs)) {
                        setSelectedRows([]);
                        setShowDeleteModal(false);
                        showSuccessToast(t("role.delete-role-success", { count: error.response.data.deletedSeqs.length }));
                        fetchData();
                    }
                }
            });
            setSelectedRows([]);
            setShowDeleteModal(false);
        } catch (error) {
            console.error("Error deleting roles:", error);
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleDeleteRole = (role) => {
        setSelectedRows([role]);
        setShowDeleteModal(true);
    };

    const onClickEditRole = (role) => {
        // Set initial data for edit mode
        setSelectedRole(role);
        setShowAddModal(true);
    };

    const handleCancelDelete = () => {
        setShowDeleteModal(false);
        setSelectedRows([]);
    };

    const handleRoleSuccess = () => {
        // Refresh the role list after successful creation/update
        fetchData();
    };

    const handleCheckboxChange = (newSelectedRows) => {
        setSelectedRows([...newSelectedRows]);
    };

    const fetchData = () => {
        const bodyRequest = {
            pageSize: itemsPerPage,
            startRow: itemsPerPage * (currentPage - 1)
        };
        getRoleList.mutate(bodyRequest);
    };

    const getRoleList = useMutationData(
        queryKeys.getRoleList,
        { method: "POST" },
        {
            onSuccess: (response) => {
                if (typeof response !== "string" && response) {
                    // Validate and clean the data before setting state
                    const cleanedData = response.object.map(item => {
                        if (typeof item === 'object' && item !== null) {
                            return {
                                ...item,
                                // Map the actual API response fields to expected fields
                                roleSeq: item.codeSeq, // Use codeSeq as roleSeq for rowKey
                                roleName: item.code, // Use code for role name
                                permissions: Array.isArray(item.permissions) ? item.permissions : [],
                                // Ensure all required fields are present
                                code: item.code || "",
                                createrName: item.createrName || "",
                                createdAt: item.createdAt || ""
                            };
                        }
                        return item;
                    });

                    // Filter out roles that have SUPERUSER permission
                    const filteredData = cleanedData.filter(item => {
                        if (Array.isArray(item.permissions)) {
                            return !item.permissions.some(permission =>
                                permission.permissionCd === "SUPERUSER"
                            );
                        }
                        return true; // Keep items without permissions array
                    });

                    setRoleList(filteredData);
                    setTotalData(response.objectCount || filteredData.length);
                    setTotalPages(Math.ceil((response.objectCount || filteredData.length) / itemsPerPage));
                }
            },
            onError: (error) => {
                console.error("Error during get data:", error);
                showErrorToast(t("sbom.fetch-error", { error: error.message || "Unknown error" }));
            },
        },
    );

    const handleItemsPerPageChangeWithReset = (newItemsPerPage) => {
        handleItemsPerPageChange(newItemsPerPage);
        setCurrentPage(1); // Reset to page 1 when changing number of rows
    };

    return (
        <div className={`flex flex-col ${colors.bgSecondary}`} data-testid="role-list-page">
            {/* Header */}
            <div className="mb-4 flex flex-row justify-between">
                <h1 className={`text-[28px] uppercase font-bold ${colors.textPrimary}`}>{t("role.list")}</h1>
                <div className="flex space-x-3">
                    <button
                        type="button"
                        data-testid="role-list-delete-button"
                        onClick={() => setShowDeleteModal(true)}
                        disabled={!selectedRows.length}
                        className={`inline-flex items-center justify-center rounded-md border px-4 py-2 h-10 text-sm font-medium whitespace-nowrap shadow-sm transition-colors ${colors.focusRing2} focus:ring-offset-2 focus:outline-none overflow-hidden ${selectedRows.length
                            ? `bg-transparent ${colors.borderDangerOutLineButton} ${colors.textDanger}`
                            : `${colors.borderPrimary} ${colors.textSecondary} cursor-not-allowed opacity-50`
                            }`}
                    >
                        <span className="truncate" title={t("role.delete-role")}>{t("role.delete-role")}</span>
                    </button>
                    <button
                        data-testid="role-list-add-button"
                        onClick={() => setShowAddModal(true)}
                        className={`font-600 inline-flex items-center justify-center rounded-md border-transparent px-4 py-2 h-10 text-sm font-semibold shadow-sm ${colors.buttonBackgroundGradient} ${colors.buttonBackgroundGradientHover} whitespace-nowrap transition-colors`}
                    >
                        {t("role.add-role")}
                    </button>
                </div>
            </div>
            {/* Role Table */}
            <div className="flex-grow min-h-0" data-testid="role-list-table">
                <TableWithPagination
                    columnConfig={columnConfig}
                    data={roleList}
                    columns={columns}
                    loading={getRoleList.isPending}
                    selectedRows={selectedRows}
                    onCheckboxChange={handleCheckboxChange}
                    disabledRow={[]}
                    hasCheckbox={!hasDeveloperPermission}
                    hasRadioButton={false}
                    rowKey="codeSeq"
                    totalPages={totalPages}
                    totalData={totalData}
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    maxRowsPerPage={itemsPerPage}
                    onItemsPerPageChange={handleItemsPerPageChangeWithReset}
                    shouldShowGoToPage={true}
                    showItemsPerPageSelector={true}
                />
            </div>

            {/* Delete Confirmation Modal */}
            <ConfirmDeleteModal
                data-testid="role-list-delete-modal"
                isOpen={showDeleteModal}
                onClose={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                title={uppercase(t("role.delete-roles-title"))}
                description={t("role.delete-role-warning", { count: selectedRows.length })}
                cancelText={t("common.cancel-button")}
                confirmText={t("common.delete-button")}
                loading={deleteLoading}
            />

            {/* Modify Role Modal */}
            <ModifyRoleModal
                data-testid="role-list-add-modal"
                isOpen={showAddModal}
                onClose={() => {
                    setShowAddModal(false);
                    setSelectedRole(null);
                }}
                onSuccess={handleRoleSuccess}
                mode={selectedRole != null ? "edit" : "create"}
                selectedRole={selectedRole}
            />
        </div>
    );
}
